import { useCallback, useEffect, useState } from "react";
import { loadSiteSettingRows, saveSiteSetting } from "@/lib/adminSettings";
import {
  DEFAULT_SITE_SETTINGS,
  primeSiteSettings,
  type HeroContent,
  type SiteBrand,
  type SiteFooterColumn,
  type SiteNavLink,
  type SiteSettings,
  type SiteSocialLink,
} from "@/lib/publicSettings";
import {
  ErrorBanner,
  Field,
  LoadingState,
  PageHeader,
  TextField,
  btnGhost,
  btnPrimary,
  textInput,
} from "@/pages/admin/components/AdminUI";
import { useAdminToast } from "@/hooks/useAdminToast";

const CARD = "bg-background-50 border border-background-200 rounded-lg p-5 md:p-6";

function isNonEmptyArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function buildSettings(rows: Record<string, unknown>): SiteSettings {
  const footer = (rows.footer || {}) as {
    footerLinks?: SiteFooterColumn[];
    socialLinks?: SiteSocialLink[];
    legalLinks?: SiteNavLink[];
  };
  return {
    brand: { ...DEFAULT_SITE_SETTINGS.brand, ...((rows.brand || {}) as Partial<SiteBrand>) },
    navLinks: isNonEmptyArray(rows.nav) ? (rows.nav as SiteNavLink[]) : DEFAULT_SITE_SETTINGS.navLinks,
    footerLinks: isNonEmptyArray(footer.footerLinks)
      ? footer.footerLinks
      : DEFAULT_SITE_SETTINGS.footerLinks,
    socialLinks: isNonEmptyArray(footer.socialLinks)
      ? footer.socialLinks
      : DEFAULT_SITE_SETTINGS.socialLinks,
    legalLinks: isNonEmptyArray(footer.legalLinks)
      ? footer.legalLinks
      : DEFAULT_SITE_SETTINGS.legalLinks,
    hero: { ...DEFAULT_SITE_SETTINGS.hero, ...((rows.home_hero || {}) as Partial<HeroContent>) },
  };
}

export default function AdminHomepage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [brand, setBrand] = useState<SiteBrand>(DEFAULT_SITE_SETTINGS.brand);
  const [hero, setHero] = useState<HeroContent>(DEFAULT_SITE_SETTINGS.hero);
  const [navLinks, setNavLinks] = useState<SiteNavLink[]>(DEFAULT_SITE_SETTINGS.navLinks);
  const [footerLinks, setFooterLinks] = useState<SiteFooterColumn[]>(DEFAULT_SITE_SETTINGS.footerLinks);
  const [socialLinks, setSocialLinks] = useState<SiteSocialLink[]>(DEFAULT_SITE_SETTINGS.socialLinks);
  const [legalLinks, setLegalLinks] = useState<SiteNavLink[]>(DEFAULT_SITE_SETTINGS.legalLinks);
  const [savingKey, setSavingKey] = useState("");
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const settings = buildSettings(await loadSiteSettingRows());
      setBrand(settings.brand);
      setHero(settings.hero);
      setNavLinks(settings.navLinks);
      setFooterLinks(settings.footerLinks);
      setSocialLinks(settings.socialLinks);
      setLegalLinks(settings.legalLinks);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load site settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refreshPublic = () => {
    primeSiteSettings({ brand, hero, navLinks, footerLinks, socialLinks, legalLinks });
  };

  const save = async (key: string, value: unknown, message: string) => {
    setSavingKey(key);
    try {
      await saveSiteSetting(key, value);
      refreshPublic();
      notify(message);
    } catch {
      setError("We couldn't save that section. Please try again.");
    } finally {
      setSavingKey("");
    }
  };

  const saveBrand = () => save("brand", brand, "Brand details updated.");
  const saveHero = () => save("home_hero", hero, "Hero content updated.");
  const saveNav = () => save("nav", navLinks, "Navigation updated.");
  const saveFooter = () =>
    save("footer", { footerLinks, socialLinks, legalLinks }, "Footer updated.");

  if (loading) {
    return (
      <div>
        <PageHeader eyebrow="Content" title="Homepage & Brand" subtitle="Loading your site settings…" />
        <LoadingState label="Loading site settings…" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Homepage & Brand"
        subtitle="The brand identity, hero, navigation and footer every visitor sees — edit here and it updates across the live website."
      />

      {error ? (
        <div className="mb-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="space-y-6">
        {/* Brand */}
        <section className={CARD}>
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-store-2-line"></i>
              </span>
              <div>
                <h2 className="font-heading text-xl text-foreground-950 font-semibold">Brand</h2>
                <p className="text-xs text-foreground-500">Name, tagline and contact details used site-wide.</p>
              </div>
            </div>
            <button type="button" onClick={saveBrand} disabled={savingKey === "brand"} className={btnPrimary}>
              {savingKey === "brand" ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              Save
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField label="Name">
              <input value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="Full name">
              <input value={brand.fullName} onChange={(e) => setBrand({ ...brand, fullName: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="Tagline">
              <input value={brand.tagline} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="Region">
              <input value={brand.region} onChange={(e) => setBrand({ ...brand, region: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="Email">
              <input value={brand.email} onChange={(e) => setBrand({ ...brand, email: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="Phone">
              <input value={brand.phone} onChange={(e) => setBrand({ ...brand, phone: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="WhatsApp number">
              <input value={brand.whatsapp} onChange={(e) => setBrand({ ...brand, whatsapp: e.target.value })} className={textInput} />
            </TextField>
          </div>
        </section>

        {/* Hero */}
        <section className={CARD}>
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-900">
                <i className="ri-layout-top-line"></i>
              </span>
              <div>
                <h2 className="font-heading text-xl text-foreground-950 font-semibold">Homepage Hero</h2>
                <p className="text-xs text-foreground-500">The first thing every visitor reads.</p>
              </div>
            </div>
            <button type="button" onClick={saveHero} disabled={savingKey === "home_hero"} className={btnPrimary}>
              {savingKey === "home_hero" ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              Save
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField label="Eyebrow">
              <input value={hero.eyebrow} onChange={(e) => setHero({ ...hero, eyebrow: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="Headline">
              <input value={hero.title} onChange={(e) => setHero({ ...hero, title: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="Headline (accent line)">
              <input value={hero.titleAccent} onChange={(e) => setHero({ ...hero, titleAccent: e.target.value })} className={textInput} />
            </TextField>
            <TextField label="Sub-headline">
              <input value={hero.subtitle} onChange={(e) => setHero({ ...hero, subtitle: e.target.value })} className={textInput} />
            </TextField>
            <div className="sm:col-span-2">
              <TextField label="Description">
                <textarea
                  value={hero.description}
                  onChange={(e) => setHero({ ...hero, description: e.target.value })}
                  rows={3}
                  maxLength={500}
                  className={`${textInput} resize-none`}
                ></textarea>
              </TextField>
            </div>
            <TextField label="Primary button label">
              <input value={hero.primaryCta.label} onChange={(e) => setHero({ ...hero, primaryCta: { ...hero.primaryCta, label: e.target.value } })} className={textInput} />
            </TextField>
            <TextField label="Primary button link">
              <input value={hero.primaryCta.href} onChange={(e) => setHero({ ...hero, primaryCta: { ...hero.primaryCta, href: e.target.value } })} className={textInput} />
            </TextField>
            <TextField label="Secondary button label">
              <input value={hero.secondaryCta.label} onChange={(e) => setHero({ ...hero, secondaryCta: { ...hero.secondaryCta, label: e.target.value } })} className={textInput} />
            </TextField>
            <TextField label="Secondary button link">
              <input value={hero.secondaryCta.href} onChange={(e) => setHero({ ...hero, secondaryCta: { ...hero.secondaryCta, href: e.target.value } })} className={textInput} />
            </TextField>
          </div>

          <div className="mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">Hero stats</p>
            <div className="space-y-2">
              {hero.stats.map((stat, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    value={stat.value}
                    onChange={(e) => {
                      const stats = hero.stats.map((s, i) => (i === index ? { ...s, value: e.target.value } : s));
                      setHero({ ...hero, stats });
                    }}
                    placeholder="20+"
                    className={`${textInput} w-28`}
                  />
                  <input
                    value={stat.label}
                    onChange={(e) => {
                      const stats = hero.stats.map((s, i) => (i === index ? { ...s, label: e.target.value } : s));
                      setHero({ ...hero, stats });
                    }}
                    placeholder="Farm Experiences"
                    className={textInput}
                  />
                  <button
                    type="button"
                    onClick={() => setHero({ ...hero, stats: hero.stats.filter((_, i) => i !== index) })}
                    aria-label="Remove stat"
                    className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 cursor-pointer shrink-0"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setHero({ ...hero, stats: [...hero.stats, { value: "", label: "" }] })}
              className={`${btnGhost} mt-3`}
            >
              <i className="ri-add-line"></i> Add stat
            </button>
          </div>
        </section>

        {/* Navigation */}
        <section className={CARD}>
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-menu-line"></i>
              </span>
              <div>
                <h2 className="font-heading text-xl text-foreground-950 font-semibold">Navigation</h2>
                <p className="text-xs text-foreground-500">Links in the top navigation bar.</p>
              </div>
            </div>
            <button type="button" onClick={saveNav} disabled={savingKey === "nav"} className={btnPrimary}>
              {savingKey === "nav" ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              Save
            </button>
          </div>
          <div className="space-y-2">
            {navLinks.map((link, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  value={link.label}
                  onChange={(e) => setNavLinks(navLinks.map((l, i) => (i === index ? { ...l, label: e.target.value } : l)))}
                  placeholder="Label"
                  className={textInput}
                />
                <input
                  value={link.href}
                  onChange={(e) => setNavLinks(navLinks.map((l, i) => (i === index ? { ...l, href: e.target.value } : l)))}
                  placeholder="/experiences"
                  className={textInput}
                />
                <button
                  type="button"
                  onClick={() => setNavLinks(navLinks.filter((_, i) => i !== index))}
                  aria-label="Remove link"
                  className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 cursor-pointer shrink-0"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setNavLinks([...navLinks, { label: "", href: "/" }])} className={`${btnGhost} mt-3`}>
            <i className="ri-add-line"></i> Add link
          </button>
        </section>

        {/* Footer */}
        <section className={CARD}>
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-700">
                <i className="ri-layout-bottom-line"></i>
              </span>
              <div>
                <h2 className="font-heading text-xl text-foreground-950 font-semibold">Footer</h2>
                <p className="text-xs text-foreground-500">Footer columns, social links and legal links.</p>
              </div>
            </div>
            <button type="button" onClick={saveFooter} disabled={savingKey === "footer"} className={btnPrimary}>
              {savingKey === "footer" ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              Save
            </button>
          </div>

          <div className="space-y-5">
            {footerLinks.map((column, colIndex) => (
              <div key={colIndex} className="border border-background-200 rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    value={column.heading}
                    onChange={(e) =>
                      setFooterLinks(footerLinks.map((c, i) => (i === colIndex ? { ...c, heading: e.target.value } : c)))
                    }
                    placeholder="Column heading"
                    className={`${textInput} font-semibold`}
                  />
                  <button
                    type="button"
                    onClick={() => setFooterLinks(footerLinks.filter((_, i) => i !== colIndex))}
                    aria-label="Remove column"
                    className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 cursor-pointer shrink-0"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
                <div className="space-y-2">
                  {column.links.map((link, linkIndex) => (
                    <div key={linkIndex} className="flex items-center gap-2">
                      <input
                        value={link.label}
                        onChange={(e) =>
                          setFooterLinks(
                            footerLinks.map((c, i) =>
                              i === colIndex
                                ? { ...c, links: c.links.map((l, li) => (li === linkIndex ? { ...l, label: e.target.value } : l)) }
                                : c,
                            ),
                          )
                        }
                        placeholder="Label"
                        className={textInput}
                      />
                      <input
                        value={link.href}
                        onChange={(e) =>
                          setFooterLinks(
                            footerLinks.map((c, i) =>
                              i === colIndex
                                ? { ...c, links: c.links.map((l, li) => (li === linkIndex ? { ...l, href: e.target.value } : l)) }
                                : c,
                            ),
                          )
                        }
                        placeholder="/path"
                        className={textInput}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setFooterLinks(
                            footerLinks.map((c, i) =>
                              i === colIndex ? { ...c, links: c.links.filter((_, li) => li !== linkIndex) } : c,
                            ),
                          )
                        }
                        aria-label="Remove footer link"
                        className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 cursor-pointer shrink-0"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFooterLinks(
                      footerLinks.map((c, i) =>
                        i === colIndex ? { ...c, links: [...c.links, { label: "", href: "/" }] } : c,
                      ),
                    )
                  }
                  className={`${btnGhost} mt-3`}
                >
                  <i className="ri-add-line"></i> Add link
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setFooterLinks([...footerLinks, { heading: "", links: [] }])}
            className={`${btnGhost} mt-3`}
          >
            <i className="ri-add-line"></i> Add column
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
            <Field label="Social links">
              <div className="space-y-2">
                {socialLinks.map((social, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      value={social.icon}
                      onChange={(e) => setSocialLinks(socialLinks.map((s, i) => (i === index ? { ...s, icon: e.target.value } : s)))}
                      placeholder="ri-instagram-line"
                      className={textInput}
                    />
                    <input
                      value={social.href}
                      onChange={(e) => setSocialLinks(socialLinks.map((s, i) => (i === index ? { ...s, href: e.target.value } : s)))}
                      placeholder="#"
                      className={textInput}
                    />
                  </div>
                ))}
              </div>
            </Field>
            <Field label="Legal links">
              <div className="space-y-2">
                {legalLinks.map((link, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      value={link.label}
                      onChange={(e) => setLegalLinks(legalLinks.map((l, i) => (i === index ? { ...l, label: e.target.value } : l)))}
                      placeholder="Privacy Policy"
                      className={textInput}
                    />
                    <input
                      value={link.href}
                      onChange={(e) => setLegalLinks(legalLinks.map((l, i) => (i === index ? { ...l, href: e.target.value } : l)))}
                      placeholder="/privacy"
                      className={textInput}
                    />
                  </div>
                ))}
              </div>
            </Field>
          </div>
        </section>
      </div>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Each section saves on its own. Changes appear on the live website immediately.
      </p>
      {node}
    </div>
  );
}